import PropTypes from "prop-types";
import React from "react";

// Helpers
import { clone } from "@Common/helpers/Modifiers/formatters";
import { DoubleCommission } from "@/PayPlans/helpers/DefaultSections";

// Hooks
import { useCallbackState } from "@Common/hooks/CallbackState";
import { useValidations } from "@Common/hooks/Validations";

// Components Layouts
import TierItemsLayout from "@/Layouts/components/Tiers/TierItemsLayout";

// MUI
import { Grid, Stack, Typography } from "@mui/material";

const PlusBeforeAllowance = (props) => {
	return (
		<Stack {...props}>
			<Typography className={"small-label mini-plus"}>+</Typography>
		</Stack>
	);
};

function Section(props) {
	const { rows = [], constraints = null, readOnly = false, onActionUpdate = () => {}, onActionValidation = () => {} } = props;

	const { noDollarAmount, noDollarPerUnitAmount } = constraints || {};

	const dataAmountDefinition = React.useMemo(() => {
		let data = [
			{
				type: "Percent",
				min: 0,
				max: 100,
				decimalsEditable: true,
				decimals: 2,
			},
		];
		if (!noDollarAmount) {
			data = [
				...data,
				{
					type: "Dollar",
					min: 0,
					max: 1000,
					decimalsEditable: false,
					decimals: 2,
				},
			];
		}
		if (!noDollarPerUnitAmount) {
			data = [
				...data,
				{
					type: "Dollar per Unit",
					min: 0,
					max: 1000,
					decimalsEditable: false,
					decimals: 2,
				},
			];
		}
		return data;
	}, [noDollarAmount, noDollarPerUnitAmount]);

	// Hooks
	const { errors, isValid, getErrorsMessages, validate } = useValidations();
	const errorsMessages = getErrorsMessages();

	// Control to send update actions
	const wasUpdated = React.useRef(false);

	const onRowsUpdate = (updatedRows) => {
		onActionValidation?.(isValid);
		if (isValid && wasUpdated.current) {
			onActionUpdate?.(updatedRows);
		}
	};

	// States
	const [innerRows, setRows] = useCallbackState([], onRowsUpdate);

	// Methods
	const handleAddTier = () => {
		const updated = [...innerRows];
		const lastAmountInput = updated?.[0]?.["amount"]?.["input"] || "Dollar";

		if (updated?.length) {
			const lastIndex = updated.length - 1;
			updated[lastIndex].to.value = 0;
		}

		updated.push(
			DoubleCommission([0, "Unit"], [100, "Unit"], {
				firstKey: "amount",
				secondKey: "allowance",
				firstCommission: [0, lastAmountInput],
				secondCommission: [0, "Dollar"],
			})
		);
		setRows(updated);
	};

	const handleDeleteTier = () => {
		const updated = [...innerRows];
		if (updated.length > 1) {
			updated.splice(-1);
			const lastIndex = updated.length;
			updated[lastIndex - 1].to.value = 100;
			save(updated);
		}
	};

	const handleUpdateValue = (row, area, value) => {
		const updated = [...innerRows];
		updated[row][area]["value"] = value ? Number(value) : 0;
		if (updated?.[row + 1] && area === "to") {
			updated[row + 1]["from"]["value"] = value ? Number(value) : 0;
		}
		save(updated);
	};

	const handleUpdateInput = (area, value) => {
		const updated = [...innerRows];
		updated?.forEach((element) => {
			element[area]["input"] = value;
			element[area]["value"] = 0;
		});
		save(updated);
	};

	const save = (updatedRows) => {
		wasUpdated.current = true;
		setRows(updatedRows);
	};

	React.useMemo(() => {
		if (innerRows?.length) {
			validate([
				{
					definition: {
						useKey: "units",
						operators: {
							conditions: [
								{
									type: "SELF",
									on: "value",
									applyOn: ["from"],
									distinct: 0,
								},
								{
									type: "INLINE",
									on: "value",
									compare: "<", // If from < to is good,
								},
								{
									type: "STEP",
									on: "value",
									compare: "===",
									deltas: { Unit: 0 }, // Add delta on upper value
								},
							],
						},
						errorsMap: ["from", "to"],
						message: "Invalid tiers",
						validationType: "DOUBLE_INPUT",
						specialProperties: {
							noFirstError: true,
							noLastError: true,
						},
					},
					section: innerRows || [],
				},
				{
					definition: {
						useKey: "amount",
						operators: {
							conditions: [
								{
									type: "SELF",
									on: "value",
									distinct: 0,
								},
								{
									type: "STEP",
									on: "value",
									compare: "<=", // If current (upper) <= next is good
									deltas: { Dollar: 0, Percent: 0 }, // Add delta on upper value
								},
							],
						},
						errorsMap: ["amount"],
						message: "Invalid amount definition",
						validationType: "SIMPLE",
						specialProperties: {
							noFirstError: true,
						},
					},
					section: innerRows || [],
				},
				{
					definition: {
						useKey: "allowance",
						operators: {
							conditions: [
								{
									type: "STEP",
									on: "value",
									compare: "<=", // If current (upper) <= next is good
									deltas: { Dollar: 0, Percent: 0 }, // Add delta on upper value
								},
							],
						},
						errorsMap: ["allowance"],
						message: "Invalid allowance definition",
						validationType: "SIMPLE",
					},
					section: innerRows || [],
				},
			]);
		}
	}, [innerRows, validate]);

	React.useEffect(() => {
		setRows(clone(rows));
		return () => {
			setRows([]);
			wasUpdated.current = false;
		};
	}, [rows, setRows]);

	return (
		<Grid container px={"32px"}>
			<Grid item xs={12} mt={"15px"}>
				<Stack width={"475px"}>
					<TierItemsLayout
						minimumRows={1}
						columnSpacing={5}
						rows={innerRows || []}
						columns={[
							{
								_id: "units",
								title: "Years of Service",
								component: {
									type: "DoubleInput",
									order: ["from", "to"],
									disableCommonInputs: true,
									properties: {
										firstInput: {
											data: [
												{
													type: "Unit",
													min: 0,
													max: 100,
													decimalsEditable: true,
													decimals: 2,
													disabled: true,
												},
											],
											sx: { maxWidth: "75px" },
										},
										secondInput: {
											data: [
												{
													type: "Unit",
													min: 0,
													max: 100,
													decimalsEditable: true,
													decimals: 2,
												},
											],
											sx: { maxWidth: "75px" },
										},
									},
									callbacks: {
										onActionUpdate: (positions, area, value) => handleUpdateValue(positions.row, area, value),
									},
								},
							},
							{
								_id: "amount",
								title: "Amt",
								component: {
									type: "InputForm",
									order: ["amount"],
									properties: {
										input: {
											data: dataAmountDefinition,
											sx: { maxWidth: "95px" },
										},
									},
									callbacks: {
										onActionUpdate: (positions, area, value) => handleUpdateValue(positions.row, area, value),
										onChangeType: (positions, area, value, type) => handleUpdateInput(area, type),
									},
								},
							},
							{
								_id: "allowance",
								title: "Demo Allowance",
								avoidDivider: true,
								sx: { marginLeft: "25px !important" },
								appendBefore: <PlusBeforeAllowance sx={{ position: "absolute", left: "-7.5px", marginTop: "3px" }} />,
								component: {
									type: "InputForm",
									order: ["allowance"],
									properties: {
										input: {
											data: [
												{
													type: "Dollar",
													min: 0,
													max: 9999999,
													decimalsEditable: false,
													decimals: 2,
												},
											],
											sx: { maxWidth: "95px" },
										},
									},
									callbacks: {
										onActionUpdate: (positions, area, value) => handleUpdateValue(positions.row, area, value),
										onChangeType: (positions, area, value, type) => handleUpdateInput(area, type),
									},
								},
							},
						]}
						errors={errors}
						errorsMessages={errorsMessages}
						readOnly={readOnly}
						onActionAdd={() => handleAddTier()}
						onActionDelete={() => handleDeleteTier()}
					/>
				</Stack>
			</Grid>
		</Grid>
	);
}
Section.propTypes = {
	rows: PropTypes.array,
	constraints: PropTypes.object,
	readOnly: PropTypes.bool,
	onActionUpdate: PropTypes.func,
	onActionValidation: PropTypes.func,
};

export default Section;
